import {useFormik} from 'formik'
import invariant from 'invariant'
import {useLocation, useNavigate} from 'react-router'

import {useAppSelector} from '../../hooks/redux'

import useGuard from '../../../../commons/src/hooks/use-guard'
import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'
import {goToDynamicProductionTimes} from '../../services/navigation'
import {
  dynamicProductionTimesToFormData,
  formDataToPartialDynamicProductionTimeRequest,
} from '../forms/dynamic-production-time/utils'
import {isMixedValue, toMixedValidationSchema, toTouchedFormData} from '../utils/form'

import {DynamicProductionTime} from '../../types'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import Stack from '../../../../commons/src/components/stack'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import DynamicProductionTimeForm, {
  MixedFormData,
  SingleFormData,
  initialValues,
  validationSchema,
} from '../forms/dynamic-production-time/form'
import PagePartial from '../page-partial'
import SaveButtonPartial from '../save-button-partial'

import ProductionTimeCalculatorPartial from './production-time-calculator-partial'

const EditDynamicProductionTimePage = () => {
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const routerLocation = useLocation()
  const locationState = routerLocation.state as
    | undefined
    | {dynamicProductionTimes: DynamicProductionTime[]}
  const dynamicProductionTimes = locationState?.dynamicProductionTimes || []

  useGuard(dynamicProductionTimes.length > 0, () => goToDynamicProductionTimes(navigate))

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...initialValues,
      ...dynamicProductionTimesToFormData(dynamicProductionTimes),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const dynamicProductionTimeRequest =
        formDataToPartialDynamicProductionTimeRequest(touchedValues)

      try {
        const dynamicProductionTimesIds = dynamicProductionTimes.map(({id}) => id)
        await dynamicProductionTimeApi.patchMultiple(
          dynamicProductionTimeRequest,
          dynamicProductionTimesIds
        )
        goToDynamicProductionTimes(navigate)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const isSomeValueForCalculatorMixed = [
    formik.values.increaseSteps,
    formik.values.maxIncrease,
    formik.values.quantityThresholds,
  ].some(isMixedValue)

  const renderTooltip = () => (
    <TooltipHandler
      openOnClick
      tooltip={
        <Tooltip>
          {dynamicProductionTimes.map((item, index) => (
            <div key={index}>{item.name}</div>
          ))}
        </Tooltip>
      }
    >
      <LinkButton>
        {dynamicProductionTimes.length} dynamicProductionTime
        {dynamicProductionTimes.length > 1 ? 's' : ''}
      </LinkButton>
    </TooltipHandler>
  )

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToDynamicProductionTimes(navigate)}
                />
                <Headline
                  size="l"
                  label="Edit dynamic production time"
                  addendum={renderTooltip()}
                  className="u-no-margin-bottom"
                />
              </ActionBar>
            }
          />
        }
      >
        <Container size="l">
          <Grid>
            <GridCell l={6}>
              <Stack space="300">
                <DynamicProductionTimeForm formik={formik} />
                <SaveButtonPartial formik={formik} />
              </Stack>
            </GridCell>
            <GridCell s={0} l={6}>
              <ProductionTimeCalculatorPartial
                dynamicProductionTimeRequest={
                  isSomeValueForCalculatorMixed
                    ? undefined
                    : formDataToPartialDynamicProductionTimeRequest(
                        formik.values as Partial<SingleFormData>
                      )
                }
              />
            </GridCell>
          </Grid>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}
export default EditDynamicProductionTimePage
